import {
    getKeyboardViewportClearance,
    KeyboardMenuPlacement,
    keepInKeyboardView,
    needsKeyboardViewportCompensation
} from "@vahak/core-ui/dist/methods/keyboardViewport";
import { isMobileViewport } from "@vahak/core-ui/dist/methods/browser";

/** Shared tuning for homepage v2 compact bottom-sheet flows (posting, bidding, auth). */
export const COMPACT_SHEET = {
    MOUNT_FOCUS_DELAY_MS: 350,
    PICKUP_TO_DROP_FOCUS_DELAY_MS: 100,
    MENU_MAX_HEIGHT: 200,
    DESKTOP_MENU_MAX_HEIGHT: 300,
    /** Extra clearance above the field gap so auth inputs clear the sticky CTA. */
    AUTH_STICKY_FOOTER_EXTRA_PX: 24
} as const;

export { getSheetScrollRootProps as getCompactSheetScrollRootProps } from "@vahak/core-ui/dist/methods/keyboardViewport";

export type CompactSheetFocusPreset = "field" | "auth";

function getCompactSheetClearance(preset: CompactSheetFocusPreset) {
    if (preset === "auth") {
        return getKeyboardViewportClearance(COMPACT_SHEET.AUTH_STICKY_FOOTER_EXTRA_PX);
    }

    return undefined;
}

/**
 * onFocus / focusin handler for compact-sheet inputs.
 * `keepInKeyboardView` runs after the keyboard settles and no-ops when the field already clears it.
 */
export function createCompactSheetInputFocusHandler(preset: CompactSheetFocusPreset = "field") {
    return (event: { target: EventTarget | null }) => {
        if (!(event.target instanceof Element)) {
            return;
        }

        keepInKeyboardView(event.target, { clearance: getCompactSheetClearance(preset) });
    };
}

export type QuoteFormLocationFieldRole = "pickup" | "drop";

export type QuoteFormLocationFieldShellConfig = {
    portalMenu: boolean;
    /**
     * iOS: custom scroll + menu placement via visualViewport.
     * Android: false — leave keyboard scroll and default placement to the browser.
     */
    keyboardAware: boolean;
    menuPlacement: KeyboardMenuPlacement;
    menuShouldScrollIntoView: boolean;
    maxMenuHeight: number;
};

function mobileQuoteFieldConfig(portalMenu: boolean): QuoteFormLocationFieldShellConfig {
    const keyboardAware = needsKeyboardViewportCompensation();

    return {
        portalMenu,
        keyboardAware,
        menuPlacement: "bottom",
        // Android native scroll; iOS uses JS scroll instead.
        menuShouldScrollIntoView: !keyboardAware,
        maxMenuHeight: COMPACT_SHEET.MENU_MAX_HEIGHT
    };
}

export function getQuoteFormLocationFieldConfig(options: {
    inSheet: boolean;
    role: QuoteFormLocationFieldRole;
    isMobile?: boolean;
}): QuoteFormLocationFieldShellConfig {
    const { inSheet, role, isMobile = isMobileViewport() } = options;

    if (!inSheet && !isMobile) {
        return {
            portalMenu: false,
            keyboardAware: false,
            menuPlacement: "bottom",
            menuShouldScrollIntoView: true,
            maxMenuHeight: COMPACT_SHEET.DESKTOP_MENU_MAX_HEIGHT
        };
    }

    // Drop in sheet: non-portaled menu stays anchored while the sheet scrolls (iOS).
    if (inSheet) {
        if (!isMobile) {
            return {
                portalMenu: true,
                keyboardAware: false,
                menuPlacement: "bottom",
                menuShouldScrollIntoView: true,
                maxMenuHeight: COMPACT_SHEET.DESKTOP_MENU_MAX_HEIGHT
            };
        }

        // Drop in mobile sheet: non-portaled menu stays anchored while the sheet scrolls (iOS).
        return mobileQuoteFieldConfig(role !== "drop");
    }

    return mobileQuoteFieldConfig(true);
}

export function getCompactSheetMountFocusDelayMs() {
    return COMPACT_SHEET.MOUNT_FOCUS_DELAY_MS;
}

export function getQuoteFormPickupToDropFocusDelayMs() {
    return COMPACT_SHEET.PICKUP_TO_DROP_FOCUS_DELAY_MS;
}

/**
 * Programmatic focus on sheet mount breaks the iOS keyboard (deep links).
 * Android can autofocus — leave that path open.
 */
export function shouldDeferCompactSheetAutoFocusToUserTap(): boolean {
    return needsKeyboardViewportCompensation();
}
